import { ButtonVariantOption } from "@/components/Button";
import { type MenuButtonProps } from "@/features/mypage/components/MenuButton";
import { type SvgIconComponent } from "@mui/icons-material";
import RequestPageIcon from "@mui/icons-material/RequestPage";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import TopicIcon from "@mui/icons-material/Topic";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import MapIcon from "@mui/icons-material/Map";
import DomainIcon from "@mui/icons-material/Domain";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import BusinessIcon from "@mui/icons-material/Business";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import { REREPO_REQUEST_PATH } from "@/features/realEstateResearchRequest/config";
import EmailIcon from "@mui/icons-material/Email";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import InfoIcon from "@mui/icons-material/Info";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import DatasetIcon from "@mui/icons-material/Dataset";

const ORDER_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLScyb6YBLZ2DRlMB4Djlef3fFHoyJKnxiO6I4RnYvjRqCBXK5w/viewform";
const MANUAL_URL =
  "https://trustart.notion.site/a11d52b037224591a30999b2a672e14b";

interface IMenuProps {
  realEstateRegistrationAggregationOfNames?: boolean;
  realEstateRegistrationMonitoring?: boolean;
  commercialBookList?: boolean;
  corporateMaster?: boolean;
  commercialCorporationList?: boolean;
  commercialRegistrationReceptionBook?: boolean;
  commercialRegistrationAggregationOfNames?: boolean;
  commercialRegistrationMonitoring?: boolean;
  commercialPictureAnalysis?: boolean;
  allMattersAnalysis?: boolean;
  dmManagement?: boolean;
  emailManagement?: boolean;
  customerManagement?: boolean;
  ownerInfoList?: boolean;
  ownerInfoViaScraping?: boolean;
  realEstateTitle?: boolean;
  rightsSectionOtsukuSearch?: boolean;
  realEstateResearchRequest?: boolean;
}

export interface BaseMenuData {
  title: string;
  level: 1 | 2;
  icon?: SvgIconComponent;
}

interface IMenuData extends BaseMenuData {
  menus: MenuButtonProps[];
}

export const getMenuData = ({
  realEstateRegistrationAggregationOfNames,
  realEstateRegistrationMonitoring,
  commercialBookList,
  corporateMaster,
  commercialCorporationList,
  commercialRegistrationReceptionBook,
  commercialRegistrationAggregationOfNames,
  commercialRegistrationMonitoring,
  commercialPictureAnalysis,
  allMattersAnalysis,
  dmManagement,
  emailManagement,
  customerManagement,
  ownerInfoList,
  realEstateTitle,
  rightsSectionOtsukuSearch,
  realEstateResearchRequest,
}: IMenuProps): IMenuData[] => {
  return [
    {
      level: 1,
      title: "不動産登記を取得する・不動産オーナーを探す",
      icon: ApartmentIcon,
      menus: [
        {
          label: "地図・住所から",
          variant: ButtonVariantOption.Contained,
          href: "/map",
          description:
            "地図から不動産登記図面の取得可能です。住居表示から地番に変換することもできます。",
          icon: MapIcon,
        },
        {
          label: "地番・家屋番号から",
          variant: ButtonVariantOption.Contained,
          href: "/acquirebook",
          description: "所有者事項、全部事項、不動産図面を請求ができます。",
          icon: RequestPageIcon,
        },
        {
          label: "地番・家屋番号リストから",
          variant: ButtonVariantOption.Contained,
          href: "/acquiremultiplebooks/upload",
          description: "所有者事項、全部事項、不動産図面を請求ができます。",
          icon: RequestQuoteIcon,
        },
        {
          label: "取得済不動産登記から",
          variant: ButtonVariantOption.Contained,
          href: "/acquirebooklist",
          description: "すでに取得済みの不動産登記図面の一覧を確認できます。",
          icon: TopicIcon,
        },
        {
          label: "不動産オーナーから",
          disabled: realEstateRegistrationAggregationOfNames,
          variant: ButtonVariantOption.Contained,
          href: "/storedpictures",
          icon: FindInPageIcon,
        },
        {
          label: "権利部（乙区）情報から",
          disabled: rightsSectionOtsukuSearch,
          variant: ButtonVariantOption.Contained,
          href: "/rightssectionotsuku",
          description:
            "全部事項の乙区（抵当権・根抵当権等）から不動産情報を検索できます。",
          icon: FindInPageIcon,
        },
        {
          label: "不動産の権利異動から",
          variant: ButtonVariantOption.Contained,
          href: "/feed",
          description:
            "不動産に関する異動（相続、売買、差し押さえ 等）の情報を閲覧できます。",
          icon: MenuBookIcon,
        },
        {
          label: "物件種別から",
          disabled: realEstateTitle,
          variant: ButtonVariantOption.Contained,
          href: "/titlesearch",
          description:
            "土地・建物に関する物理的状況（表題部）から不動産情報を検索できます。",
          icon: LocationCityIcon,
        },
        {
          label: "不動産登記\nモニタリング",
          disabled: realEstateRegistrationMonitoring,
          href: "/monitoring",
          description:
            "あらかじめ登録した不動産が異動したかどうかをモニタリングすることができます。",
          variant: ButtonVariantOption.Contained,
          icon: MonitorHeartIcon,
        },
        {
          label: "オペレーターに依頼",
          variant: ButtonVariantOption.Contained,
          href: ORDER_URL,
          description: "所有者事項・全部事項のデータ化の発注ができます。",
          icon: TrendingUpIcon,
        },
      ],
    },
    {
      level: 2,
      title: "不動産登記データ化",
      icon: DatasetIcon,
      menus: [
        {
          label: "所有者事項\nデータ化",
          variant: ButtonVariantOption.Contained,
          href: "/ownerinfo/upload",
          disabled: ownerInfoList,
          description:
            "所有者事項のPDFをアップロードすることで、エクセルデータ化が行えます。",
          icon: PictureAsPdfIcon,
        },
        {
          label: "所有者事項\nデータ化結果一覧",
          variant: ButtonVariantOption.Contained,
          href: "/ownerinfo/list",
          disabled: ownerInfoList,
          description:
            "すでにエクセルデータ化済みの所有者事項の一覧を確認できます。",
          icon: LibraryBooksIcon,
        },
        {
          label: "全部事項\nデータ化",
          variant: ButtonVariantOption.Contained,
          href: "/allmatters/upload",
          disabled: allMattersAnalysis,
          description:
            "全部事項のPDFをアップロードすることで、エクセルデータ化が行えます。",
          icon: PictureAsPdfIcon,
        },
        {
          label: "全部事項\nデータ化結果一覧",
          variant: ButtonVariantOption.Contained,
          href: "/allmatters/list",
          disabled: allMattersAnalysis,
          description:
            "すでにエクセルデータ化済みの全部事項の一覧を確認できます。",
          icon: LibraryBooksIcon,
        },
      ],
    },
    {
      level: 1,
      title: "商業登記を取得する",
      icon: CorporateFareIcon,
      menus: [
        {
          label: "法人情報から",
          href: "/corporate-master",
          disabled: corporateMaster,
          variant: ButtonVariantOption.Contained,
          description:
            "法人名・所在地・業種などから法人を検索し、属性・業績・商業登記を確認できます。",
          icon: DomainIcon,
        },
        // 法人情報の画面から移れるため、法人情報を使えるユーザーには出さない
        ...(corporateMaster
          ? [
              {
                label: "法人名から",
                href: "/commercialbook",
                disabled: commercialBookList,
                variant: ButtonVariantOption.Contained,
                description: "登記事項（商業・法人登記）を請求ができます。",
                icon: RequestPageIcon,
              },
            ]
          : []),
        ...(commercialCorporationList
          ? [
              {
                label: "法人リストから",
                href: "/commercialbook/corporationlist",
                disabled: commercialBookList,
                variant: ButtonVariantOption.Contained,
                description:
                  "CSV・Excelの法人リストから商業登記をまとめて取得できます。",
                icon: RequestPageIcon,
              },
            ]
          : []),
        {
          label: "取得済商業登記から",
          href: "/commercialbook/list",
          disabled: commercialBookList,
          variant: ButtonVariantOption.Contained,
          description: "すでに取得済みの商業登記の一覧を確認できます。",
          icon: TopicIcon,
        },
        {
          label: "取締役名から",
          disabled: commercialRegistrationAggregationOfNames,
          variant: ButtonVariantOption.Contained,
          icon: FindInPageIcon,
        },
        {
          label: "法人の権利異動から",
          href: "/commercialregistrationreceptionbook",
          disabled: commercialRegistrationReceptionBook,
          variant: ButtonVariantOption.Contained,
          icon: MenuBookIcon,
        },
        {
          label: "商業登記\nモニタリング",
          disabled: commercialRegistrationMonitoring,
          href: "/commercial-monitoring",
          variant: ButtonVariantOption.Contained,
          icon: MonitorHeartIcon,
        },
        {
          label: "オペレーターに依頼",
          disabled: true,
          variant: ButtonVariantOption.Contained,
          icon: TrendingUpIcon,
        },
      ],
    },
    {
      level: 2,
      title: "商業登記データ化",
      icon: DatasetIcon,
      menus: [
        {
          label: "商業登記\nデータ化",
          variant: ButtonVariantOption.Contained,
          href: "/commercialpictureanalysis/upload",
          disabled: commercialPictureAnalysis,
          description:
            "商業登記のPDFをアップロードすることで、エクセルデータ化が行えます。",
          icon: PictureAsPdfIcon,
        },
        {
          label: "商業登記\nデータ化結果一覧",
          variant: ButtonVariantOption.Contained,
          href: "/commercialpictureanalysis/list",
          disabled: commercialPictureAnalysis,
          description:
            "すでにエクセルデータ化済みの商業登記の一覧を確認できます。",
          icon: LibraryBooksIcon,
        },
      ],
    },
    {
      level: 1,
      title: "顧客管理",
      icon: BusinessIcon,
      menus: [
        {
          label: "顧客管理",
          variant: ButtonVariantOption.Contained,
          href: "/customermanagement/customerList",
          disabled: customerManagement,
          description:
            "顧客管理しているオーナーのご情報をもとに、メール送付をしたりテンプレ管理をしたりできます。",
          icon: ManageSearchIcon,
        },
        {
          label: "メール管理",
          variant: ButtonVariantOption.Contained,
          href: "/emailManagement/mail/savedEmailList",
          disabled: emailManagement,
          description:
            "顧客管理しているオーナーのご情報をもとに、メール送付をしたりテンプレ管理をしたりできます。",
          icon: EmailIcon,
        },
        {
          label: "DM管理",
          href: "/dmManagement/orders",
          disabled: dmManagement,
          variant: ButtonVariantOption.Contained,
          icon: ContactMailIcon,
          description: "DMの発送を依頼できます。",
        },
      ],
    },
    {
      level: 1,
      title: "その他",
      icon: MoreHorizIcon,
      menus: [
        // 他の引数と同じく true は「使えない」。送信先がつながるまではグレー表示にせず、項目ごと出さない
        ...(realEstateResearchRequest
          ? []
          : [
              {
                label: "不動産調査依頼",
                variant: ButtonVariantOption.Contained,
                href: REREPO_REQUEST_PATH,
                description:
                  "不動産の簡易・詳細調査、売買契約書作成、重説チェックを依頼できます。",
                icon: ManageSearchIcon,
              },
            ]),
        {
          label: "アカウント管理",
          variant: ButtonVariantOption.Contained,
          href: "/account",
          description: "消費クレジットの閲覧とパスワード変更ができます。",
          icon: ContactPageIcon,
        },
        {
          label: "マニュアル",
          variant: ButtonVariantOption.Contained,
          href: MANUAL_URL,
          description: "R.E.DATA Plusの操作マニュアルです。",
          icon: AutoStoriesIcon,
        },
        {
          label: "お問い合わせ",
          variant: ButtonVariantOption.Contained,
          actionType: "contact-dialog",
          description: "TRUSTARTの運営チームにお問い合わせができます。",
          icon: InfoIcon,
        },
      ],
    },
  ];
};

export const replaceToSemiBlank = (text: string): string => {
  return text.replace(/\\n/g, " ");
};
